import styled from "styled-components";

type TextStyle = "title" | "subtitle" | "paragraph";

const DS_PREFIX: Record<TextStyle, string> = {
  title: "title",
  subtitle: "subtitle",
  paragraph: "paragraph",
};

const dsVar = (style: TextStyle | undefined, suffix: string) => {
  const prefix = DS_PREFIX[style || "paragraph"];
  return `var(--${prefix}-${suffix})`;
};

export const StyledEditorWrapper = styled.div<{
  $horizontalAlignment?: string;
}>`
  break-inside: avoid;
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  justify-content: ${({ $horizontalAlignment }) =>
    $horizontalAlignment || "inherit"};

  .resize-controls {
    .counter {
      opacity: 1;
      visibility: visible;
      transform: translate(-50%, 0);
    }
    button.height {
      display: none;
    }
  }
`;

export const StyledEditorMain = styled.div<{ $width?: number }>`
  width: ${({ $width }) => $width || 100}%;
  position: relative;
  @media screen and (max-width: 768px) {
    width: 100%;
  }
  .rich-text-element {
    width: 100% !important;
  }
`;

export const StyledEditorHeader = styled.div<{
  $fontSize?: string | number;
  $color?: string;
  $horizontalAlignment?: string;
  $isBold?: boolean;
  $isItalic?: boolean;
  $isUnderline?: boolean;
  $textStyle?: TextStyle;
}>`
  cursor: pointer;
  text-align: left !important;
  display: flex;
  flex-direction: ${({ $horizontalAlignment }) =>
    $horizontalAlignment === "end" ? "row-reverse" : "row"};
  justify-content: ${({ $horizontalAlignment }) => $horizontalAlignment};
  align-items: center;

  font-size: ${({ $fontSize, $textStyle }) =>
    $fontSize ? `${$fontSize}px` : dsVar($textStyle, "size")} !important;
  font-family: ${({ $textStyle }) => dsVar($textStyle, "font")} !important;
  color: ${({ $color, $textStyle }) => $color || dsVar($textStyle, "color")};
  line-height: var(--default-line-height) !important;

  ${({ $isBold, $textStyle }) =>
    $isBold !== undefined
      ? $isBold
        ? "font-weight: bold !important;"
        : ""
      : `font-weight: ${dsVar($textStyle, "style")} !important;`}
  ${({ $isItalic }) => $isItalic && "font-style: italic !important;"}
  ${({ $isUnderline }) =>
    $isUnderline && "text-decoration: underline !important;"}

  @media screen and (max-width: 768px) {
    font-size: ${({ $textStyle }) =>
      dsVar($textStyle, "mobile-size")} !important;
  }

  p {
    font-size: inherit;
    color: inherit;
    font-weight: inherit;
    font-style: inherit;
    text-decoration: inherit;
    line-height: inherit;
    font-family: inherit;
    margin: 0;
  }

  /* Rich Text */
  & > :nth-child(1) {
    min-height: 33px;
    width: auto;
    justify-content: ${({ $horizontalAlignment }) =>
      $horizontalAlignment === "center" ? "center" : "start"};
  }

  /* Icon */
  & > :nth-child(2) {
    padding: ${({ $horizontalAlignment }) =>
      $horizontalAlignment !== "end" ? "0px 0px 0px 10px" : "0px 10px 0px 0px"};
  }

  .content {
    text-align: ${({ $horizontalAlignment }) =>
      $horizontalAlignment === "start"
        ? "left"
        : $horizontalAlignment === "end"
          ? "right"
          : "center"} !important;
    *:not(.fr-placeholder),
    .rich-text-wrapper {
      text-align: inherit !important;
      color: inherit !important;
      font-size: inherit !important;
      font-weight: inherit !important;
      font-style: inherit !important;
      text-decoration: inherit !important;
      line-height: 1 !important;
    }
    p,
    h2,
    h3 {
      margin-bottom: 0 !important;
    }
  }
`;

export const StyledEditorContent = styled.div<{ $isExpanded?: boolean }>`
  display: grid;
  grid-template-rows: ${({ $isExpanded }) => ($isExpanded ? "1fr" : "0fr")};

  margin-top: ${({ $isExpanded }) => ($isExpanded ? "16px" : "0px")};
  transition:
    grid-template-rows 300ms,
    margin 300ms;

  & > div {
    overflow: hidden;
  }
`;
